import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, List, Icon, Segment, Checkbox, Label } from 'semantic-ui-react';
import { StyledModal } from './SharedStyles';
import * as api from '../services/api';

const EVENT_TYPES = [
  { key: 'task.completed', label: 'Task Completed' },
  { key: 'task.created', label: 'Task Created' },
  { key: 'task.updated', label: 'Task Updated' },
  { key: 'task.deleted', label: 'Task Deleted' },
  { key: 'list.all_completed', label: 'All Items Completed' },
  { key: 'delegates.assigned', label: 'Delegates Changed' },
];

const WebhookModal = ({ open, onClose }) => {
  const [webhooks, setWebhooks] = useState([]);
  const [name, setName] = useState('');
  const [url, setUrl] = useState('');
  const [selectedEvents, setSelectedEvents] = useState([]);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  useEffect(() => {
    if (open) {
      loadWebhooks();
      setName('');
      setUrl('');
      setSelectedEvents([]);
    }
  }, [open]);

  const loadWebhooks = async () => {
    try {
      const data = await api.getWebhooks();
      setWebhooks(data);
    } catch (err) {
      console.error('Error loading webhooks:', err);
    }
  };

  const toggleEvent = (eventKey) => {
    setSelectedEvents(prev =>
      prev.includes(eventKey) ? prev.filter(e => e !== eventKey) : [...prev, eventKey]
    );
  };

  const handleCreate = async () => {
    if (!url.trim() || selectedEvents.length === 0) return;
    try {
      await api.createWebhook({ name: name.trim(), url: url.trim(), events: selectedEvents });
      setName('');
      setUrl('');
      setSelectedEvents([]);
      loadWebhooks();
    } catch (err) {
      console.error('Error creating webhook:', err);
    }
  };

  const handleToggleActive = async (webhook) => {
    try {
      await api.updateWebhook(webhook.id, { active: !webhook.active });
      loadWebhooks();
    } catch (err) {
      console.error('Error toggling webhook:', err);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.deleteWebhook(id);
      setConfirmDeleteId(null);
      loadWebhooks();
    } catch (err) {
      console.error('Error deleting webhook:', err);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'Never';
    return new Date(dateStr).toLocaleDateString();
  };

  const truncateUrl = (u) => {
    if (u.length <= 50) return u;
    return u.substring(0, 47) + '...';
  };

  return (
    <StyledModal open={open} onClose={onClose}>
      <Modal.Header>Outbound Webhooks</Modal.Header>
      <Modal.Content>
        <Segment secondary style={{ borderRadius: '12px', marginBottom: '1em' }}>
          <p style={{ margin: 0, color: '#666', fontSize: '0.9em' }}>
            Didit will POST a JSON payload to your URL when selected events occur.
            Works with IFTTT Webhooks, Zapier, n8n, or any HTTP endpoint.
          </p>
        </Segment>

        <Form onSubmit={(e) => { e.preventDefault(); handleCreate(); }}>
          <Form.Input
            placeholder="Name (e.g., IFTTT Checklist Done)"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
          <Form.Input
            placeholder="https://maker.ifttt.com/trigger/{event}/json/with/key/{key}"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
          />
          <div style={{ marginBottom: '1em' }}>
            <label style={{ fontWeight: 'bold', fontSize: '0.9em', display: 'block', marginBottom: '0.5em' }}>
              Events:
            </label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75em' }}>
              {EVENT_TYPES.map(evt => (
                <Checkbox
                  key={evt.key}
                  label={evt.label}
                  checked={selectedEvents.includes(evt.key)}
                  onChange={() => toggleEvent(evt.key)}
                />
              ))}
            </div>
          </div>
          <Button type="submit" primary disabled={!url.trim() || selectedEvents.length === 0}>
            Add Webhook
          </Button>
        </Form>

        <List divided relaxed style={{ marginTop: '1.5em' }}>
          {webhooks.map(w => (
            <List.Item key={w.id}>
              <List.Content>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '0.3em' }}>
                  <div>
                    <strong>{w.name || 'Unnamed'}</strong>
                    <span style={{ marginLeft: '0.75em', color: '#999', fontSize: '0.85em' }}>
                      {truncateUrl(w.url)}
                    </span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em' }}>
                    <Checkbox
                      toggle
                      checked={w.active}
                      onChange={() => handleToggleActive(w)}
                      style={{ marginRight: '0.5em' }}
                    />
                    {confirmDeleteId === w.id ? (
                      <>
                        <Button size="mini" negative onClick={() => handleDelete(w.id)}>Confirm</Button>
                        <Button size="mini" onClick={() => setConfirmDeleteId(null)}>Cancel</Button>
                      </>
                    ) : (
                      <Icon name="trash" link color="red" onClick={() => setConfirmDeleteId(w.id)} />
                    )}
                  </div>
                </div>
                <div style={{ marginBottom: '0.3em' }}>
                  {w.events.map(e => (
                    <Label key={e} size="tiny" style={{ marginRight: '0.3em' }}>
                      {EVENT_TYPES.find(et => et.key === e)?.label || e}
                    </Label>
                  ))}
                </div>
                <span style={{ color: '#999', fontSize: '0.8em' }}>
                  Created {formatDate(w.createdAt)} &middot; Last triggered {formatDate(w.lastTriggeredAt)}
                </span>
              </List.Content>
            </List.Item>
          ))}
          {webhooks.length === 0 && (
            <List.Item>
              <List.Content>
                <em>No webhooks configured. Add one above.</em>
              </List.Content>
            </List.Item>
          )}
        </List>
      </Modal.Content>
      <Modal.Actions>
        <Button onClick={onClose}>Close</Button>
      </Modal.Actions>
    </StyledModal>
  );
};

export default WebhookModal;
